/**
 * assets/ready-to-feel-yourself.js
 * Ready to Feel Like Yourself Again Section - Redesigned
 * Performance-optimized JavaScript with refined interactions
 * Follows Nutriop Longevity's premium technical standards
 */

class ReadyFeelYourselfSection {
  constructor() {
    this.section = document.querySelector('.ready-feel-yourself');
    if (!this.section) return;
    
    this.init();
  }
  
  init() {
    // Initialize intersection observer for animation on scroll
    this.setupIntersectionObserver();
    
    // Track CTA clicks for analytics
    this.trackCTAClicks();
    
    // Enhance accessibility
    this.enhanceAccessibility();
    
    // Setup subtle scroll effects
    this.setupScrollEffects();
    
    // Initialize card interactions
    this.setupCardInteractions();
    
    // Setup performance monitoring
    this.monitorPerformance();
  }
  
  setupIntersectionObserver() {
    const observerOptions = {
      root: null,
      rootMargin: '-50px',
      threshold: 0.1
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Add animation class when section comes into view
          entry.target.classList.add('is-visible');
          
          // Fire GA4 event for section visibility
          if (typeof gtag !== 'undefined') {
            gtag('event', 'view_section', {
              'event_category': 'engagement',
              'event_label': 'ready_feel_yourself',
              'section_name': 'ready_feel_yourself',
              'section_position': 'pre_numbers_speak'
            });
          }
          
          // Stop observing after first view
          observer.unobserve(entry.target);
          
          // Mark animations as complete after they finish
          setTimeout(() => {
            entry.target.classList.add('animation-complete');
          }, 1000);
        }
      });
    }, observerOptions);
    
    observer.observe(this.section);
  }
  
  trackCTAClicks() {
    const ctaButtons = this.section.querySelectorAll('.ready-feel-yourself__button');
    
    ctaButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const buttonText = button.querySelector('.ready-feel-yourself__button-text').textContent;
        const isPrimary = button.classList.contains('ready-feel-yourself__button--primary');
        const buttonType = isPrimary ? 'quiz' : 'shop_all';
        
        // GA4 Enhanced eCommerce tracking
        if (typeof gtag !== 'undefined') {
          gtag('event', 'select_promotion', {
            'creative_name': 'ready_feel_yourself_section',
            'creative_slot': isPrimary ? 'primary_cta' : 'secondary_cta',
            'promotion_id': buttonType,
            'promotion_name': buttonText,
            'items': [{
              'item_id': buttonType,
              'item_name': buttonText,
              'creative_name': 'homepage_cta',
              'creative_slot': isPrimary ? 'left' : 'right',
              'promotion_name': 'Ready to Feel Yourself'
            }]
          });
          
          // Also track as custom event
          gtag('event', 'cta_click', {
            'event_category': 'engagement',
            'event_label': buttonText,
            'cta_type': buttonType,
            'cta_position': 'ready_feel_yourself'
          });
        }
        
        // Klaviyo tracking for premium customer behaviors
        if (typeof klaviyo !== 'undefined') {
          klaviyo.push(['track', 'Homepage CTA Clicked', {
            'CTA Section': 'Ready to Feel Yourself',
            'CTA Type': isPrimary ? 'Quiz' : 'Shop All',
            'CTA Text': buttonText,
            'Page': window.location.pathname,
            'Timestamp': new Date().toISOString()
          }]);
          
          // Track as potential high-intent user
          if (isPrimary) {
            klaviyo.push(['track', 'Quiz Interest Shown', {
              'Source': 'Homepage',
              'Section': 'Ready to Feel Yourself'
            }]);
          }
        }
      });
    });
  }
  
  enhanceAccessibility() {
    // Add keyboard navigation enhancements
    const cards = this.section.querySelectorAll('.ready-feel-yourself__cta-card');
    
    cards.forEach((card, index) => {
      // Make cards keyboard focusable
      card.setAttribute('tabindex', '0');
      card.setAttribute('role', 'article');
      card.setAttribute('aria-label', `Option ${index + 1} of 2`);
      
      // Add keyboard event listeners
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const button = card.querySelector('.ready-feel-yourself__button');
          if (button) button.click();
        }
        
        // Arrow key navigation between cards
        if (e.key === 'ArrowRight' && cards[index + 1]) {
          cards[index + 1].focus();
        } else if (e.key === 'ArrowLeft' && cards[index - 1]) {
          cards[index - 1].focus();
        }
      });
      
      // Add focus styles dynamically
      card.addEventListener('focus', () => {
        card.style.outline = '3px solid #FF6B6B';
        card.style.outlineOffset = '4px';
      });
      
      card.addEventListener('blur', () => {
        card.style.outline = 'none';
      });
    });
    
    // Ensure proper ARIA labels for screen readers
    this.section.setAttribute('role', 'region');
    this.section.setAttribute('aria-label', 'Get started with Nutriop Longevity');
    
    // Add skip link for keyboard users
    const skipLink = document.createElement('a');
    skipLink.href = '#numbers-speak';
    skipLink.className = 'skip-link';
    skipLink.textContent = 'Skip to statistics';
    skipLink.style.cssText = 'position: absolute; left: -9999px; top: auto; width: 1px; height: 1px; overflow: hidden;';
    skipLink.addEventListener('focus', () => {
      skipLink.style.left = '20px';
      skipLink.style.width = 'auto';
      skipLink.style.height = 'auto';
    });
    skipLink.addEventListener('blur', () => {
      skipLink.style.left = '-9999px';
    });
    this.section.insertBefore(skipLink, this.section.firstChild);
  }
  
  setupScrollEffects() {
    // Subtle parallax effect for premium feel
    if (window.matchMedia('(min-width: 768px)').matches && 
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      
      let ticking = false;
      
      const handleScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            const scrolled = window.pageYOffset;
            const sectionTop = this.section.offsetTop;
            const sectionHeight = this.section.offsetHeight;
            
            // Only apply effect when section is in viewport
            if (scrolled > sectionTop - window.innerHeight && 
                scrolled < sectionTop + sectionHeight) {
              
              this.section.classList.add('is-scrolled');
              
              // Subtle parallax for accent line
              const accentLine = this.section.querySelector('.ready-feel-yourself__accent-line');
              if (accentLine) {
                const speed = 0.3;
                const yPos = -(scrolled * speed);
                accentLine.style.transform = `translateX(-50%) translateY(${yPos}px)`;
              }
            }
            
            ticking = false;
          });
          ticking = true;
        }
      };
      
      window.addEventListener('scroll', handleScroll, { passive: true });
    }
  }
  
  setupCardInteractions() {
    const cards = this.section.querySelectorAll('.ready-feel-yourself__cta-card');
    
    cards.forEach(card => {
      // Track hover interactions
      let hoverStartTime;
      
      card.addEventListener('mouseenter', () => {
        hoverStartTime = Date.now();
      });
      
      card.addEventListener('mouseleave', () => {
        if (hoverStartTime) {
          const hoverDuration = Date.now() - hoverStartTime;
          
          // Track extended hovers as interest signals
          if (hoverDuration > 2000 && typeof gtag !== 'undefined') {
            const isPrimary = card.classList.contains('ready-feel-yourself__cta-card--primary');
            gtag('event', 'extended_hover', {
              'event_category': 'engagement',
              'event_label': isPrimary ? 'quiz_card' : 'shop_card',
              'value': Math.round(hoverDuration / 1000)
            });
          }
        }
      });
      
      // Add touch feedback for mobile
      card.addEventListener('touchstart', () => {
        card.style.transform = 'scale(0.98)';
      });
      
      card.addEventListener('touchend', () => {
        card.style.transform = '';
      });
    });
  }
  
  monitorPerformance() {
    // Monitor Core Web Vitals for this section
    if ('PerformanceObserver' in window) {
      // Monitor Largest Contentful Paint
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          
          // Send LCP data to analytics if element is in this section
          if (lastEntry.element?.closest('.ready-feel-yourself')) {
            const lcpTime = Math.round(lastEntry.startTime);
            
            if (typeof gtag !== 'undefined') {
              gtag('event', 'web_vitals', {
                'event_category': 'Web Vitals',
                'event_label': 'LCP',
                'value': lcpTime,
                'section': 'ready_feel_yourself',
                'metric_value': lcpTime,
                'metric_rating': lcpTime < 2500 ? 'good' : lcpTime < 4000 ? 'needs-improvement' : 'poor'
              });
            }
            
            // Log to console in development
            if (window.location.hostname === 'localhost') {
              console.log('Ready Feel Yourself LCP:', lcpTime, 'ms');
            }
          }
        });
        
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        // LCP observer not supported
      }
      
      // Monitor First Input Delay
      try {
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            if (entry.target?.closest('.ready-feel-yourself')) {
              const fidValue = Math.round(entry.processingStart - entry.startTime);
              
              if (typeof gtag !== 'undefined') {
                gtag('event', 'web_vitals', {
                  'event_category': 'Web Vitals',
                  'event_label': 'FID',
                  'value': fidValue,
                  'section': 'ready_feel_yourself',
                  'metric_value': fidValue,
                  'metric_rating': fidValue < 100 ? 'good' : fidValue < 300 ? 'needs-improvement' : 'poor'
                });
              }
            }
          });
        });
        
        fidObserver.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        // FID observer not supported
      }
    }
    
    // Monitor interaction performance
    this.monitorInteractionPerformance();
  }
  
  monitorInteractionPerformance() {
    const buttons = this.section.querySelectorAll('.ready-feel-yourself__button');
    
    buttons.forEach(button => {
      button.addEventListener('click', (e) => {
        const startTime = performance.now();
        
        // Monitor how long the interaction takes
        requestAnimationFrame(() => {
          const endTime = performance.now();
          const duration = endTime - startTime;
          
          // Log slow interactions
          if (duration > 100) {
            console.warn('Slow interaction detected:', {
              element: 'ready-feel-yourself-button',
              duration: Math.round(duration),
              target: e.target
            });
          }
        });
      });
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ReadyFeelYourselfSection();
  });
} else {
  new ReadyFeelYourselfSection();
}

// Export for use in other modules if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ReadyFeelYourselfSection;
}

// Add to global scope for debugging in development
if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
  window.ReadyFeelYourselfSection = ReadyFeelYourselfSection;
}